(() => {
  if (window.SportovaTariffCart) return;

  const isSurcharge = (item) =>
    item?.properties?._sportova_tariff_surcharge === 'true' || item?.handle === 'sportova-tariff-surcharge';

  const loadTariffMap = () => {
    if (window.SPORTOVA_TARIFF_SURCHARGE) return Promise.resolve(window.SPORTOVA_TARIFF_SURCHARGE);
    if (window.sportovaTariffMapPromise) return window.sportovaTariffMapPromise;
    window.sportovaTariffMapPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = window.sportovaTariffMapAssetUrl;
      script.onload = () => window.SPORTOVA_TARIFF_SURCHARGE ? resolve(window.SPORTOVA_TARIFF_SURCHARGE) : reject(new Error('Tariff map unavailable'));
      script.onerror = () => reject(new Error('Tariff map could not load'));
      document.head.appendChild(script);
    });
    return window.sportovaTariffMapPromise;
  };

  const request = async (url, body) => {
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || data.errors || 'Cart update failed');
    return data;
  };

  const sync = async ({ forceMap = false } = {}) => {
    let cart = await request(`${window.routes?.cart_url || '/cart'}.js`);
    const hasTariffSignal = cart.items.some((item) =>
      isSurcharge(item) ||
      item.properties?._usa_tariff_surcharge_cents ||
      item.properties?.['USA tariff surcharge cents']
    );
    if (!forceMap && !hasTariffSignal && !window.SPORTOVA_TARIFF_SURCHARGE) return cart;
    const config = await loadTariffMap();
    const desired = new Map();
    let changed = false;

    cart.items.filter((item) => !isSurcharge(item)).forEach((item) => {
      const variantId = String(item.variant_id);
      const mapped = config.productVariantMap?.[variantId];
      const amountCents = Number(
        item.properties?._usa_tariff_surcharge_cents ||
        item.properties?.['USA tariff surcharge cents'] ||
        mapped?.amountCents ||
        0
      );
      if (!amountCents) return;
      const surchargeVariantId = String(config.amountVariantMap?.[amountCents] || mapped?.surchargeVariantId || '');
      if (!surchargeVariantId) throw new Error(`No tariff charge exists for ${amountCents} cents`);
      const entry = desired.get(variantId) || { parentVariantId: variantId, surchargeVariantId, amountCents, quantity: 0 };
      if (entry.surchargeVariantId !== surchargeVariantId) throw new Error(`Conflicting tariff amounts for variant ${variantId}`);
      entry.quantity += Number(item.quantity || 0);
      desired.set(variantId, entry);
    });

    const updates = {};
    const satisfied = new Set();
    cart.items.filter(isSurcharge).forEach((item) => {
      const parentVariantId = String(item.properties?._sportova_tariff_parent_variant_id || '');
      const expected = desired.get(parentVariantId);
      if (!expected || String(item.variant_id) !== expected.surchargeVariantId || satisfied.has(parentVariantId)) {
        updates[item.key] = 0;
        return;
      }
      satisfied.add(parentVariantId);
      if (Number(item.quantity) !== expected.quantity) updates[item.key] = expected.quantity;
    });

    if (Object.keys(updates).length) {
      changed = true;
      cart = await request(window.routes?.cart_update_url || '/cart/update.js', { updates });
    }

    const missing = [...desired.values()].filter((entry) => !satisfied.has(entry.parentVariantId) && entry.quantity > 0);
    if (missing.length) {
      changed = true;
      cart = await request(window.routes?.cart_add_url || '/cart/add.js', {
        items: missing.map((entry) => ({
          id: Number(entry.surchargeVariantId),
          quantity: entry.quantity,
          properties: {
            _sportova_tariff_surcharge: 'true',
            _sportova_tariff_parent_variant_id: entry.parentVariantId,
            _sportova_tariff_amount_cents: String(entry.amountCents),
          },
        })),
      });
    }

    const verified = await request(`${window.routes?.cart_url || '/cart'}.js`);
    const verifiedFees = verified.items.filter(isSurcharge);
    for (const entry of desired.values()) {
      const quantity = verifiedFees
        .filter((item) => String(item.properties?._sportova_tariff_parent_variant_id || '') === entry.parentVariantId)
        .filter((item) => String(item.variant_id) === entry.surchargeVariantId)
        .reduce((total, item) => total + Number(item.quantity || 0), 0);
      if (quantity !== entry.quantity) throw new Error(`Tariff charge verification failed for variant ${entry.parentVariantId}`);
    }
    verified._sportovaTariffChanged = changed;
    return verified;
  };

  let checkoutPromise = null;
  const proceedToCheckout = async (control) => {
    if (checkoutPromise) return checkoutPromise;
    control?.setAttribute('aria-disabled', 'true');
    control?.classList.add('loading');
    checkoutPromise = sync({ forceMap: true })
      .then(() => {
        const destination = new URL(window.routes?.checkout_url || '/checkout', window.location.origin);
        const form = control?.form || control?.closest('form') || control?.closest('cart-drawer')?.querySelector('form');
        const scope = form || document;
        const codes = [...scope.querySelectorAll('[data-sportova-trade-discount-input]')]
          .flatMap((input) => String(input.value || '').split(','))
          .map((code) => code.trim()).filter(Boolean);
        if (codes.length) destination.searchParams.set('discount', [...new Set(codes)].join(','));
        window.location.assign(destination.toString());
      })
      .catch((error) => {
        console.error('Sportova tariff checkout sync failed', error);
        const message = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (message) message.textContent = 'We could not verify the required tariff surcharge. Please refresh the cart and try again.';
        control?.removeAttribute('aria-disabled');
        control?.classList.remove('loading');
        checkoutPromise = null;
      });
    return checkoutPromise;
  };

  window.SportovaTariffCart = { sync, loadMap: loadTariffMap };

  const reconcileCartPage = () => {
    if (!/^\/cart\/?$/.test(window.location.pathname)) return;
    sync({ forceMap: true }).then((cart) => {
      if (cart._sportovaTariffChanged) window.location.reload();
    }).catch((error) => console.error('Sportova tariff cart-page sync failed', error));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcileCartPage, { once: true });
  else reconcileCartPage();

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('[name="checkout"], #CartDrawer-Checkout, a[href$="/checkout"]');
    if (!checkout) return;
    const scope = checkout.closest('cart-drawer, form') || document;
    const acknowledgement = scope.querySelector('[data-cart-acknowledgement]');
    if (acknowledgement && !acknowledgement.checked) return;
    event.preventDefault();
    proceedToCheckout(checkout);
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.submitter?.name !== 'checkout') return;
    const acknowledgement = event.target.querySelector('[data-cart-acknowledgement]');
    if (acknowledgement && !acknowledgement.checked) return;
    event.preventDefault();
    proceedToCheckout(event.submitter);
  }, true);
})();
